import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Layers, Plus, Search, SlidersHorizontal } from 'lucide-react';

import Button from '@/shared/components/Button';
import Input from '@/shared/components/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/dialog';
import '@/styles/design-system.css';

import {
  ROUTE_BACK_OFFICE_LOGIN,
  ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT,
  ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_NEW,
} from '../../../constants';
import {
  ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK,
  ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE,
  OPERATOR_AND,
  OPERATOR_EQUAL,
} from '../../../dao/dbConstants';
import { searchEntityRecords } from '../../../utils/entityApiUtils';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import InstructionBlocksSection, { hasVerticalRule, referencesVertical } from './InstructionBlocksSection';
import { isContentPromptCategory } from './promptCategoryUtils';

const SCOPE_BASE = 'base';

const groupKey = (row) => `${row.category ?? ''}::${row.name ?? ''}`;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const PromptTemplates = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(null);
  const [query, setQuery] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeQuery, setCustomizeQuery] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [searchResult, blocksResult, metaRes] = await Promise.all([
          searchEntityRecords(ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE, {
            pageNumber: 1,
            pageSize: 1000,
            ignoreLimit: true,
            group: {
              operator: OPERATOR_AND,
              criteria: [{ fieldName: 'type', operator: OPERATOR_EQUAL, value: 'system' }],
            },
          }),
          searchEntityRecords(ENTITY_NAME_BACK_OFFICE_INSTRUCTION_BLOCK, {
            pageNumber: 1,
            pageSize: 1000,
            ignoreLimit: true,
          }),
          fetch(`${SERVER_URL}/api/ui/bo/instruction-blocks/meta`, { credentials: 'include' }),
        ]);
        // Only keep content-generation prompts (overridable per vertical)
        const allRows = searchResult?.data?.list ?? [];
        setRows(allRows.filter((row) => isContentPromptCategory(row.category)));
        setBlocks(blocksResult?.data?.list ?? []);
        if (metaRes.ok) {
          const meta = await metaRes.json();
          setVerticals(meta?.verticals ?? []);
        }
      } catch (error) {
        console.error('PromptTemplates: failed to load prompts', error);
        showErrorMessage('Failed to load prompt templates.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const baseRows = useMemo(
    () =>
      rows
        .filter((row) => !row.masterCompanyTypeId && !row.intent)
        .sort((a, b) => groupKey(a).localeCompare(groupKey(b))),
    [rows]
  );

  const intentOnlyRows = useMemo(
    () =>
      rows
        .filter((row) => !row.masterCompanyTypeId && row.intent)
        .sort((a, b) => groupKey(a).localeCompare(groupKey(b))),
    [rows]
  );

  const overridesByVertical = useMemo(() => {
    const map = new Map();
    rows
      .filter((row) => row.masterCompanyTypeId)
      .forEach((row) => {
        const key = Number(row.masterCompanyTypeId);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      });
    map.forEach((list) => list.sort((a, b) => groupKey(a).localeCompare(groupKey(b))));
    return map;
  }, [rows]);

  const baseBlockCount = useMemo(
    () => blocks.filter((block) => !hasVerticalRule(block)).length,
    [blocks]
  );

  const blockCountByVertical = useMemo(() => {
    const map = new Map();
    verticals.forEach((vertical) => {
      map.set(
        Number(vertical.id),
        blocks.filter((block) => referencesVertical(block, vertical.id)).length
      );
    });
    return map;
  }, [blocks, verticals]);

  const selectedVertical = useMemo(
    () => (scope && scope !== SCOPE_BASE ? verticals.find((v) => Number(v.id) === Number(scope)) : null),
    [scope, verticals]
  );

  const matchesQuery = (row) => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return (
      (row.category ?? '').toLowerCase().includes(needle) ||
      (row.name ?? '').toLowerCase().includes(needle)
    );
  };

  const getFilenameFromDisposition = (disposition) => {
    if (!disposition) return null;
    const match = disposition.match(/filename="([^"]+)"/i);
    return match?.[1] || null;
  };

  const onDownloadAll = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetch(`${SERVER_URL}/api/ui/bo/prompts/export-md-zip`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response?.status === 401 || response?.status === 403) {
        window.location.href = ROUTE_BACK_OFFICE_LOGIN;
        return;
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        showErrorMessage(errorBody?.message || 'Failed to download prompt templates.');
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename =
        getFilenameFromDisposition(response.headers.get('content-disposition')) ||
        'prompt-templates.zip';
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccessMessage('Prompt templates download started.');
    } catch (error) {
      console.error('PromptTemplates: download failed', error);
      showErrorMessage('Failed to download prompt templates.');
    } finally {
      setIsDownloading(false);
    }
  };

  const openPrompt = (row, verticalId = null, intentValue = null) => {
    const params = new URLSearchParams();
    if (verticalId) params.set('vertical', verticalId);
    if (intentValue) params.set('intent', intentValue);
    const suffix = params.toString() ? `?${params}` : '';
    navigate(`${ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT}${row.id}${suffix}`);
  };

  const openScope = (nextScope) => {
    setQuery('');
    setScope(nextScope);
  };

  const renderPromptTable = (list, { dimmed = false, verticalId = null, showIntent = false } = {}) => (
    <div className="overflow-hidden !rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white !shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="!border-b !border-solid !border-[#cbd5e1] !bg-[#f8fafc] text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-3">Area</th>
            <th className="px-4 py-3">Prompt</th>
            {showIntent && <th className="px-4 py-3 w-32">Intent</th>}
            <th className="px-4 py-3 w-36">Last Updated</th>
            <th className="px-4 py-3 w-10" />
          </tr>
        </thead>
        <tbody>
          {list.map((row) => (
            <tr
              key={row.id}
              onClick={() => openPrompt(row, verticalId, row.intent ?? null)}
              className={`cursor-pointer !border-b !border-solid !border-[#e2e8f0] last:!border-b-0 transition-colors hover:!bg-[#eef2f6] ${dimmed ? 'text-muted-foreground' : 'text-foreground'}`}
            >
              <td className="px-4 py-3">{row.category}</td>
              <td className="px-4 py-3 font-medium">{row.name}</td>
              {showIntent && (
                <td className="px-4 py-3 text-muted-foreground">{row.intent || '—'}</td>
              )}
              <td className="px-4 py-3 text-muted-foreground">{formatDate(row.updatedOn)}</td>
              <td className="px-4 py-3 text-muted-foreground">
                <ChevronRight className="size-4" />
              </td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr>
              <td colSpan={showIntent ? 5 : 4} className="px-4 py-8 text-center text-muted-foreground">
                No prompts found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderCards = () => (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">Content Prompts</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Prompts used for content generation. Base prompts apply to every vertical. A vertical can
          override specific prompts — everything it doesn&apos;t override falls back to base.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <button
          type="button"
          onClick={() => openScope(SCOPE_BASE)}
          className="group flex flex-col !rounded-xl !border-2 !border-solid !border-[#8db2dd] !bg-[#eef4fb] p-5 text-left !shadow-sm transition-all hover:!border-primary hover:!shadow-md"
        >
          <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Layers className="size-5" />
          </div>
          <div className="text-base font-semibold text-foreground">Base Prompts</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Default prompts used by all verticals
          </div>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
            {baseRows.length} prompt{baseRows.length === 1 ? '' : 's'}
            <span className="text-muted-foreground">·</span>
            {baseBlockCount} block{baseBlockCount === 1 ? '' : 's'}
            <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </button>

        {verticals.map((vertical) => {
          const overrideCount = overridesByVertical.get(Number(vertical.id))?.length ?? 0;
          const blockCount = blockCountByVertical.get(Number(vertical.id)) ?? 0;
          return (
            <button
              key={vertical.id}
              type="button"
              onClick={() => openScope(Number(vertical.id))}
              className="group flex flex-col !rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white p-5 text-left !shadow-sm transition-all hover:!border-primary/60 hover:!shadow-md"
            >
              <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-[#eef2f6] text-foreground">
                <SlidersHorizontal className="size-5" />
              </div>
              <div className="text-base font-semibold text-foreground">{vertical.name}</div>
              <div className="mt-1 text-sm text-muted-foreground">
                {overrideCount > 0
                  ? 'Overrides some prompts, rest use base'
                  : 'No overrides yet — uses base prompts'}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm">
                {overrideCount > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    {overrideCount} customized
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-[#f1f5f9] px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    All base
                  </span>
                )}
                {blockCount > 0 && (
                  <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                    {blockCount} block{blockCount === 1 ? '' : 's'}
                  </span>
                )}
                <ChevronRight className="ml-auto size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          );
        })}
      </div>
    </>
  );

  const renderBaseList = () => {
    const list = baseRows.filter(matchesQuery);
    return (
      <>
        <InstructionBlocksSection scope="base" verticals={verticals} onBlocksChange={setBlocks} />
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="mr-auto flex items-center gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
              Prompts
            </h3>
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {baseRows.length}
            </span>
          </div>
          <Input
            icon={<Search className="size-4 text-muted-foreground" />}
            placeholder="Search prompts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName="w-64"
          />
          <Button
            variant="secondary"
            leftIcon={<Download className="size-4" />}
            onClick={onDownloadAll}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download All'}
          </Button>
          <Button
            leftIcon={<Plus className="size-4" />}
            onClick={() => navigate(ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_NEW)}
          >
            Add Prompt
          </Button>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Every vertical uses these prompts unless it has its own override.
        </p>
        {renderPromptTable(list)}

        {intentOnlyRows.length > 0 && (
          <>
            <div className="mb-3 mt-8 flex items-center gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                Intent Overrides
              </h3>
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                {intentOnlyRows.length}
              </span>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              Apply to a single intent across every vertical — everything else falls back to base.
            </p>
            {renderPromptTable(intentOnlyRows.filter(matchesQuery), { showIntent: true })}
          </>
        )}
      </>
    );
  };

  const renderCustomizeDialog = () => {
    const overriddenKeys = new Set(
      (overridesByVertical.get(Number(scope)) ?? []).map((row) => groupKey(row))
    );
    const needle = customizeQuery.trim().toLowerCase();
    const candidates = baseRows
      .filter((row) => !overriddenKeys.has(groupKey(row)))
      .filter(
        (row) =>
          !needle ||
          (row.category ?? '').toLowerCase().includes(needle) ||
          (row.name ?? '').toLowerCase().includes(needle)
      );

    return (
      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="th w-[560px] max-w-[92vw] p-6">
          <DialogHeader className="mb-4">
            <DialogTitle>Customize a prompt for {selectedVertical?.name}</DialogTitle>
          </DialogHeader>
          <Input
            icon={<Search className="size-4 text-muted-foreground" />}
            placeholder="Search prompts..."
            value={customizeQuery}
            onChange={(e) => setCustomizeQuery(e.target.value)}
            autoFocus
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Pick a base prompt to create a {selectedVertical?.name}-specific version. The base
            prompt stays untouched.
          </p>
          <div className="mt-4 max-h-[50vh] overflow-y-auto !rounded-xl !border !border-solid !border-[#cbd5e1]">
            {candidates.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => openPrompt(row, Number(scope))}
                className="flex w-full cursor-pointer items-center gap-3 !border-b !border-solid !border-[#e2e8f0] !bg-white px-4 py-3 text-left transition-colors last:!border-b-0 hover:!bg-[#eef2f6]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">{row.name}</div>
                  <div className="text-xs text-muted-foreground">{row.category}</div>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
            {candidates.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No prompts found.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const renderVerticalList = () => {
    const allOverrides = overridesByVertical.get(Number(scope)) ?? [];

    return (
      <>
        <InstructionBlocksSection
          scope={Number(scope)}
          verticalName={selectedVertical?.name}
          onBlocksChange={setBlocks}
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="mr-auto flex items-center gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
              Customized Prompts
            </h3>
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
              {allOverrides.length}
            </span>
          </div>
          <Button
            leftIcon={<Plus className="size-4" />}
            onClick={() => {
              setCustomizeQuery('');
              setCustomizeOpen(true);
            }}
          >
            Customize Prompt
          </Button>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Prompts customized for {selectedVertical?.name}. All other prompts fall back to base.
        </p>
        {renderPromptTable(allOverrides, { showIntent: true })}

        {renderCustomizeDialog()}
      </>
    );
  };

  const renderListHeader = () => (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <Button variant="secondary" size="icon" onClick={() => openScope(null)} aria-label="Back to verticals">
        <ChevronLeft className="size-5" />
      </Button>
      <div className="mr-auto">
        <h2 className="text-xl font-semibold text-foreground">
          {scope === SCOPE_BASE ? 'Base Prompts' : selectedVertical?.name ?? 'Vertical'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {scope === SCOPE_BASE ? 'Apply to all verticals' : 'Vertical-specific prompt overrides'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="th !overflow-visible">
      <div className="mx-auto max-w-6xl px-6 py-8">
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading prompts...</div>
        ) : scope === null ? (
          renderCards()
        ) : (
          <>
            {renderListHeader()}
            {scope === SCOPE_BASE ? renderBaseList() : renderVerticalList()}
          </>
        )}
      </div>
    </div>
  );
};

export default PromptTemplates;